import {
  AcquisitionChannel,
  Currency,
  getMetricMerchantViewsByCriteria,
  LoanStatusExternal,
  MetricMerchantView,
} from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';

import { CurrencyFilter } from '@/components/modules/Metrics/shared/entities/Filter/CurrencyFilter';
import { EndDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/EndDateFilter';
import { LoanStatusExternalFilter } from '@/components/modules/Metrics/shared/entities/Filter/LoanStatusExternalFilter';
import { StartDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/StartDateFilter';
import { QueryBuilder } from '@/components/modules/Metrics/shared/entities/QueryBuilder';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';

interface UseMetricMerchantViewsProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal[];
  acquisitionChannel?: AcquisitionChannel[];
}

export const useMetricMerchantViews = ({ currency, timeInterval, loanStatusExternal }: UseMetricMerchantViewsProps) => {
  const {
    state: { metricMerchantQueryBuilder: queryBuilder, metricMerchantViews, metricsRefreshToken },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const [hasRequested, setHasRequested] = useState(false);
  const [filteredMetrics, setFilteredMetrics] = useState<MetricMerchantView[] | undefined>(undefined);
  const lastToken = useRef(metricsRefreshToken);

  const setQueryBuilder = (queryBuilder: QueryBuilder) =>
    updateOperationsContext({ metricMerchantQueryBuilder: queryBuilder });

  useEffect(() => {
    const force = lastToken.current !== metricsRefreshToken;
    lastToken.current = metricsRefreshToken;
    getMetricAggregateViews(force);
  }, [timeInterval, loanStatusExternal, metricsRefreshToken]);

  const getMetricAggregateViews = async (force = false) => {
    const newQueryBuilder = new QueryBuilder([
      new CurrencyFilter([currency]),
      new StartDateFilter([timeInterval]),
      new EndDateFilter([timeInterval]),
      new LoanStatusExternalFilter(loanStatusExternal),
    ]);

    if (force || !queryBuilder || queryBuilder.needsRequest(newQueryBuilder)) {
      const query = newQueryBuilder.merge(queryBuilder).createQuery();
      if (!force) setHasRequested(false);
      try {
        const newMetrics = await getMetricMerchantViewsByCriteria({ query });
        updateOperationsContext({ metricMerchantViews: newMetrics });
        const filteredMetrics = newQueryBuilder.filterMetrics(newMetrics);
        setFilteredMetrics(filteredMetrics);
        setQueryBuilder(newQueryBuilder.merge(queryBuilder));
      } catch (error) {}
    } else {
      if (metricMerchantViews) {
        const filteredMetrics = newQueryBuilder.filterMetrics(metricMerchantViews);
        setFilteredMetrics(filteredMetrics);
      }
    }
    setHasRequested(true);
  };

  return { filteredMetrics, hasRequested };
};
